import { Helmet } from 'react-helmet';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectIsLoggedIn } from '@/store/reducers/user/userSlice';
import { ROUTES } from '@/constants/routes';
import MainLayout from '@/layouts/MainLayout';
import PageNotFound from '@/pages/PageNotFound';
import DownloadAppPage from '@/pages/DownloadAppPage';
import PlaidFailedPage from '@/pages/PlaidFailedPage';
import SurveyPage from '@/pages/SurveyPage';
import ShareCollectionPage from '@/pages/ShareCollectionPage';
import ShareLayout from '@/layouts/ShareLayout';
import SignUpPage from '@/pages/SignUpPage';

type PrivateRouteProps = {
  children: JSX.Element;
};

function PrivateRoute({ children }: PrivateRouteProps) {
  const location = useLocation();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);

  if (!isLoggedIn) {
    return <Navigate to={ROUTES.SURVEY} state={{ from: location }} replace />;
  }

  return children;
}

function RootRoute() {
  const location = useLocation();

  return <Navigate to={ROUTES.SURVEY} state={{ from: location }} replace />;
}

const Router = createBrowserRouter([
  {
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <RootRoute />,
      },
      {
        path: ROUTES.SURVEY,
        element: (
          <>
            <Helmet>
              <title>Findies - Survey</title>
            </Helmet>
            <SurveyPage />
          </>
        ),
      },
      {
        path: ROUTES.DOWNLOAD_APP,
        element: (
          <>
            <Helmet>
              <title>Findies - Download app</title>
            </Helmet>
            <DownloadAppPage />
          </>
        ),
      },
      {
        element: (
          <PrivateRoute>
            <Outlet />
          </PrivateRoute>
        ),
        children: [
          {
            path: ROUTES.PLAID_FAILED,
            element: (
              <>
                <Helmet>
                  <title>Findies - Failed</title>
                </Helmet>
                <PlaidFailedPage />
              </>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <ShareLayout />,
    children: [
      {
        path: ROUTES.SHARE_COLLECTION,
        element: (
          <>
            <Helmet>
              <title>Findies - Collection</title>
            </Helmet>
            <ShareCollectionPage />
          </>
        ),
      },
      {
        path: ROUTES.SIGN_UP,
        element: (
          <>
            <Helmet>
              <title>Findies - Sign up</title>
            </Helmet>
            <SignUpPage />
          </>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <>
        <Helmet>
          <title>Findies - Page not found</title>
        </Helmet>
        <PageNotFound />
      </>
    ),
  },
]);

export default Router;
